import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Store } from '@ngrx/store';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { NgxPermissionsService } from 'ngx-permissions';
import { filter, Observable, Subject, take, takeUntil } from 'rxjs';

import * as fromStore from 'src/app/store/';
import { environment } from 'src/environments/environment';
import { AuthService } from './core/services/auth';
import { StateService } from './core/services/state';
import { User } from './models/user.model';
import { SessionExpiredComponent } from './shared/components';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title: string = 'MyPortal';

  loading: boolean = true;

  permissions: string[] = [];

  // How long a user can be inactive before considered idle, in seconds.
  sessionLengthInSeconds: number = 900; // 15 minutes.

  // How long a user can be idle before considered timed out, in seconds.
  timeoutWarningSeconds: number = 60; // 1 minute.

  timedOut: boolean = false;

  lastPing: Date = null;

  browserSize$: Observable<string> = this.store.select(
    fromStore.getBrowserSizeName,
  );

  browserSize: string;

  loggedInUser$: Observable<User>;

  displayLoadingSpinner: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private idle: Idle,
    private keepAlive: Keepalive,
    private router: Router,
    private store: Store<fromStore.State>,
    private state: StateService,
    private gtmService: GoogleTagManagerService,
    private authService: AuthService,
    private permissionsService: NgxPermissionsService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.browserSize$.pipe(takeUntil(this.destroy$)).subscribe((name) => {
      if (name) {
        this.browserSize = name;
      }
    });
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        this.checkRouterEvent(event);

        if (
          (environment.production || environment.dev) &&
          event instanceof NavigationEnd
        ) {
          const gtmTag = { page_path: event.urlAfterRedirects };
          this.gtmService.pushTag(gtmTag);
        }
      });

    this.initSessionTimeouts();
    this.state
      .getState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((overlayState: string) => {
        if (overlayState === 'show_loading_overlay') {
          this.toggleLoadingOverlay(true);
        } else {
          this.toggleLoadingOverlay(false);
        }
      });

    await this.authService.validateAndLinkOktaSession();

    this.loggedInUser$ = this.store.select(fromStore.getCurrentUser);
    this.loggedInUser$
      .pipe(
        filter((user) => user != null),
        take(1),
      )
      .subscribe(async (user: User) => {
        if (user?.Permissions?.length) {
          this.permissions = user.Permissions;
          this.permissionsService.loadPermissions(this.permissions);
        } else if (user?.Id && !user?.Permissions?.length) {
          this.redirectToAccessDenied();
        }

        if (await this.authService.isLoggedIn()) {
          this.authService.trackUser(user);
          this.idle.watch();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  redirectToAccessDenied(): void {
    window.location.pathname = '/access-denied';
  }

  initSessionTimeouts(): void {
    // Set idle and timeout limits.
    // See if there is an expires entry in local storage
    const expires = localStorage.getItem('ng2Idle.main.expiry');
    if (expires) {
      const timestamp = Number(expires);
      const expireDate = new Date(timestamp);
      const remainingSession = Math.floor(
        (expireDate.getTime() - Date.now()) / 1000,
      );

      // If the user has time left in their session from a previous visit or another tab
      // Set the timeout from localstorage
      if (remainingSession > 0) {
        this.idle.setIdle(remainingSession);
      } else {
        this.idle.setIdle(this.sessionLengthInSeconds);
      }
    } else {
      this.idle.setIdle(this.sessionLengthInSeconds);
    }

    this.idle.setTimeout(this.timeoutWarningSeconds);

    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // No longer idle
    this.idle.onIdleEnd.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetSession();
    });

    // Timed out
    this.idle.onTimeout.pipe(takeUntil(this.destroy$)).subscribe(async () => {
      this.timedOut = true;
      this.dialog.closeAll();
      await this.authService.signOut('expired');
    });

    // User is idle
    this.idle.onIdleStart.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.idle.clearInterrupts();
      this.openDialog();
    });

    // dialog countdown start
    this.idle.onTimeoutWarning
      .pipe(takeUntil(this.destroy$))
      .subscribe((/* countdown */) => {});

    // sets the ping interval
    this.keepAlive.interval(this.sessionLengthInSeconds);
    this.keepAlive.onPing.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.lastPing = new Date();
      return true;
    });
  }

  checkRouterEvent(routerEvent: Event | RouterEvent): void {
    if (routerEvent instanceof NavigationStart) {
      this.loading = true;
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.loading = false;
    }
  }

  private resetSession(): void {
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();
    this.timedOut = false;
  }

  openDialog(): void {
    // Dialog for session timeout
    const dialogConfig = {
      autoFocus: true,
      minHeight: '315px',
      width: this.browserSize === 'small' ? 'calc(100vw - 20px)' : '650px',
      maxWidth: '650px',
      data: {
        browserSize: this.browserSize,
      },
      disableClose: true,
    };

    const sessionExpiredDialog = this.dialog.open(
      SessionExpiredComponent,
      dialogConfig,
    );

    sessionExpiredDialog
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (staySignedIn: boolean) => {
        if (staySignedIn) {
          this.resetSession();
        } else {
          await this.authService.signOut('expired');
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  private toggleLoadingOverlay(show: boolean): void {
    this.displayLoadingSpinner = show;
    this.changeDetectorRef.detectChanges();
  }
}
